import { useState } from "react"
import { router } from "@inertiajs/react"
import { Check, Copy, FastForward, Link2, MessageSquare } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { formatDate } from "./bookStatus"

export const LEADER_STATUS = {
  not_sent: { label: "Leader: not sent", className: "bg-zinc-100 text-zinc-700" },
  waiting: { label: "Leader: waiting", className: "bg-amber-50 text-amber-800" },
  approved: { label: "Leader: approved", className: "bg-emerald-50 text-emerald-800" },
  skipped: { label: "Leader: skipped (test)", className: "bg-violet-50 text-violet-800" },
}

export function LeaderBadge({ status }) {
  const meta = LEADER_STATUS[status] || LEADER_STATUS.not_sent
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${meta.className}`}>{meta.label}</span>
}

function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button size="sm" variant="outline" onClick={async () => {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }}>
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : label}
    </Button>
  )
}

// The Profiled leader's approval: the coach makes a private link and sends it
// from their own email or phone. The leader reads, approves once, and may
// send notes. A badge, never a chapter status.
export function LeaderCard({ book, chapter }) {
  const leader = chapter.leader
  const firstName = chapter.leaderName.split(/\s+/)[0]
  const message = leader.link
    ? `Hi ${firstName}, your chapter in ${book.title} is ready for you to read. Please read it and approve it here, or send me a note if anything needs to change: ${leader.link}`
    : ""
  const skip = () => {
    if (window.confirm(`Skip ${firstName}'s approval? This is for testing only. The chapter counts as approved for Send to print, and it shows as "skipped" to you and to Kingdom Factor. ${firstName} is not asked or told.`)) {
      router.post(`/app/coach/books/${book.id}/chapters/${chapter.id}/skip_leader_approval`, {}, { preserveScroll: true })
    }
  }
  const makeLink = () => {
    if (!leader.link || window.confirm("Make a new link? The old link will stop working.")) {
      router.post(`/app/coach/books/${book.id}/chapters/${chapter.id}/leader_link`, {}, { preserveScroll: true })
    }
  }

  return (
    <Card>
      <CardContent className="space-y-3 text-sm">
        <div className="flex items-center justify-between">
          <b>{chapter.leaderName}'s approval</b>
          <LeaderBadge status={leader.status} />
        </div>
        {leader.status === "approved" && (
          <p className="text-xs text-zinc-600">Approved {formatDate(leader.approvedAt)}. Later changes do not need a new approval.</p>
        )}
        {leader.status === "skipped" && (
          <p className="text-xs text-zinc-600">Skipped for testing on {formatDate(leader.approvedAt)}. {firstName} did not read or approve this chapter.</p>
        )}
        {leader.status === "not_sent" && (
          <p className="text-xs text-zinc-600">
            {leader.canSend ? `Send ${firstName} a private link to read the chapter and approve it. No login needed.` : `Approve the chapter first. Then send ${firstName} a private link to read it.`}
          </p>
        )}
        {leader.link && !["approved", "skipped"].includes(leader.status) && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input readOnly value={leader.link} className="text-xs" onFocus={(e) => e.target.select()} aria-label="Leader link" />
              <CopyButton text={leader.link} label="Copy" />
            </div>
            <div className="rounded-md bg-zinc-50 p-2 text-xs text-zinc-700 [overflow-wrap:anywhere]">{message}</div>
            <CopyButton text={message} label="Copy the message" />
            <p className="text-xs text-zinc-500">Send it from your own email or phone. The app does not email {firstName}.</p>
          </div>
        )}
        {leader.note && (
          <div className="rounded-md border p-2">
            <div className="mb-1 inline-flex items-center gap-1 text-xs text-zinc-500"><MessageSquare className="h-3.5 w-3.5" /> {firstName}'s note, {formatDate(leader.noteAt)}</div>
            <p className="whitespace-pre-line text-sm">{leader.note}</p>
          </div>
        )}
        {leader.canSend && !["approved", "skipped"].includes(leader.status) && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={leader.link ? "ghost" : "default"} onClick={makeLink}>
              <Link2 className="h-4 w-4" /> {leader.link ? "Make a new link" : "Make the leader's link"}
            </Button>
            {leader.canSkip && (
              <Button size="sm" variant="outline" onClick={skip}>
                <FastForward className="h-4 w-4" /> Skip leader approval (testing)
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
